import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { PASSWORD_PUBLIC_PATHS } from '@/services/authPasswordPaths'

// Transient-failure retry (backend restart / brief 5xx). GET only, capped.
const MAX_RETRIES = 2
const RETRY_BACKOFF_MS = [400, 1200]

type RetriableConfig = InternalAxiosRequestConfig & { __retryCount?: number }

const isRetriable = (error: AxiosError): boolean => {
  const method = (error.config?.method ?? 'get').toLowerCase()
  if (method !== 'get') return false
  // No response = network / connection refused / backend restarting
  if (!error.response) return true
  const s = error.response.status
  return s >= 500 && s < 600
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/** Keep in sync with `AUTH_STORAGE_KEYS.token` in `authSlice` (avoid importing slice here → circular `authService` → `api`). */
export const AUTH_TOKEN_STORAGE_KEY = 'authToken'
/** Keep in sync with `AUTH_TRANSPORT_KEY` in `authSlice`. */
export const AUTH_TRANSPORT_STORAGE_KEY = 'launchpad_auth_transport'

export interface ApiError {
  message: string
  status?: number
}

// src/services/api.ts
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true, // ← this sends cookies on every request
})

const PUBLIC_AUTH_PATHS = [
  '/auth/signup',
  '/auth/verify-otp',
  '/auth/set-password',
  '/auth/login',
  '/auth/google',
  '/users/login',
  '/leaderboard/home',
  ...PASSWORD_PUBLIC_PATHS,
]

api.interceptors.request.use((config) => {
  config.headers['Content-Type'] = 'application/json'

  const path = config.url ?? ''
  const isPublicAuth = PUBLIC_AUTH_PATHS.some((p) => path.includes(p))
  const hasAuthHeader = Boolean(
    config.headers.Authorization ??
    (config.headers as { authorization?: string }).authorization,
  )

  if (isPublicAuth || hasAuthHeader) {
    return config
  }

  if (typeof window !== 'undefined') {
    const transport = localStorage.getItem(AUTH_TRANSPORT_STORAGE_KEY)
    const useCookieOnly = transport === 'cookie'
    if (!useCookieOnly) {
      const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
      if (token?.trim()) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }
  }

  return config
})

type ApiErrorBody = {
  message?: string
  Message?: string
  error?: string
  detail?: string
  title?: string
}

function messageFromResponseData(data: unknown): string | null {
  if (data == null || typeof data !== 'object') return null
  const d = data as ApiErrorBody
  const candidates = [d.message, d.Message, d.error, d.detail, d.title]
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().length > 0) return c.trim()
  }
  return null
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const config = error.config as RetriableConfig | undefined

    if (config && isRetriable(error)) {
      const attempt = config.__retryCount ?? 0
      if (attempt < MAX_RETRIES) {
        config.__retryCount = attempt + 1
        await sleep(RETRY_BACKOFF_MS[attempt] ?? 1200)
        return api.request(config)
      }
    }

    const fromBody = error.response?.data
      ? messageFromResponseData(error.response.data)
      : null
    const typedError: ApiError = {
      message: fromBody ?? error.message ?? 'Unexpected API error',
      status: error.response?.status,
    }
    return Promise.reject(typedError)
  },
)
